import { InterviewAnswerMode } from '../../core/interview/interview';
import { interviewLanguages } from '../../core/interview/interview-language';

export function getInterviewPrompt(
  company: string,
  language: string,
  position: string,
  companyDetails: string,
  jobDescription: string,
  resume: string,
  question: string,
  conversationList: string[],
  config: {
    additionalDetails?: string;
    selectedAnswerMode?: InterviewAnswerMode;
    previousCodingQuestion?: {
      codeQuestion?: string;
      codeIdea?: string;
      codeSolution?: string;
      codeTestCases?: string;
    };
  }
) {
  const concisePrompt = `Create a concise response that highlights and summarizes the key aspects of the question. Use bullet points if necessary. No need to repeat the question.`;

  let prompt = `
You should act interviewee being interviewed in a live interview for \`\`\`${position}\`\`\` position at \`\`\`${company}\`\`\`.
Your response should be using the resume provided as source of truth to form a relevant response to the details provided about the company description and the job role description.
You should respond to questions and conversations directly to the details provided about the company description and the job role description.

You should use language \`\`\`${interviewLanguages.find((c) => c.code === language)?.language ?? 'English'}\`\`\` to write your responses.

You should respond in spoken language, easy for people to understand, and clear.

You answer should be concise and to the point with no repetition of the question.

The company details are:
\`\`\`
${companyDetails}
\`\`\`

The job role description is:
\`\`\`
${jobDescription}
\`\`\`

The resume you will use as your source of truth is:
\`\`\`
${resume}
\`\`\`

Follow the interview Guidelines below to response to the interviewer's question:
- List the clarification questions you might ask to better understand this interviewer's question.
- Ensure that you only respond to questions about your past projects listed in your resume when they pertain to your professional background.
- During the interview, you might be asked to discuss your professional background. Please focus your responses on your previous projects. Provide detailed descriptions of these projects. This approach helps clearly illustrate your contributions and the outcomes of your work.
- If you're asked about experience you don't have during an interview, try these strategies:
    1. **Acknowledge the Gap**: Be honest about lacking specific experience.
    2. **Highlight Transferable Skills**: Discuss skills from other areas that are applicable to the role.
    3. **Show Enthusiasm to Learn**: Express your eagerness to learn and adapt quickly.
    4. **Use Hypothetical Scenarios**: Describe how you would handle related situations based on your knowledge.
    5. **Discuss Related Projects**: Mention any relevant projects or volunteer work that could demonstrate your capability.
    6. **Ask Clarifying Questions**: Use the interview as a chance to learn exactly what the interviewer is looking for and tailor your responses accordingly.
    7. **Thinking in STAR (Situation, Task, Action, Result)**: When faced with a complex question, use the STAR method to analyze it—consider the Situation, Task, Action, and Result. However, you don’t need to present your answer in the STAR format.
- If a question is incomplete, unclear, or if you need more information, ask the interviewer for clarification.
- ${config.selectedAnswerMode === InterviewAnswerMode.Concise ? concisePrompt : 'Create a concise response. No need to repeat the question.'}

`;

  if (config.previousCodingQuestion) {
    prompt += `
### Previous Coding Question:

### Code Question:
\`\`\`
${config.previousCodingQuestion.codeQuestion}
\`\`\`

### Code Solution:
\`\`\`
${config.previousCodingQuestion.codeSolution}
\`\`\`
      `;
  }

  if (conversationList && conversationList.length > 0) {
    prompt += `
## Previous Conversations:
\`\`\`
  ${conversationList.join('\n')}

\`\`\`

`;
  }

  if (config.additionalDetails) {
    prompt += `Please also consider the last minute details:
\`\`\`
  ${config.additionalDetails}
\`\`\`

  `;
  }

  prompt += `
## Interviewer's Question:
\`\`\`
  ${question}
\`\`\`

`;

  if (conversationList && conversationList.length > 0) {
    prompt += `
## Attention:
Focus on the current question, setting aside earlier topics unless they are directly relevant to the discussion at hand.`;
  }

  prompt += `

Interviewee's Response:
  `;

  return prompt;
}
